import {makeAutoObservable, observable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {PaymentExpandedCommon} from '../../../../api/payment';
import type {
  Destructible,
  RequireInitialLoad,
  WithReactionDispose,
} from '../../../../utils/store';

import {paymentExpandCommon} from '../../../../api/payment';
import {alert} from '../../../../components/alert';
import {paymentFetch} from '../../../../services/entity-action/payment/payment.fetch';
import {emitter} from '../../../../utils/eventEmitter';
import {
  InitialLoadManager,
  ReactionDisposeManager,
} from '../../../../utils/store';
import {PaymentModel} from '../../Payment.model';
import {
  MAKE_REFUND_FAIL_EVENT,
  MAKE_REFUND_SUCCESS_EVENT,
} from '../MakeRefund/utils';

type Config = {
  /**
   * Callback to call router.goBack() for example
   */
  onFetchError?: () => void;
  payment?: PaymentExpandedCommon;
  paymentId?: string;
};

export class PaymentInfoStore
  implements Destructible, RequireInitialLoad, WithReactionDispose
{
  disposer = new ReactionDisposeManager();
  initialLoad = new InitialLoadManager();
  payment: null | PaymentModel = null;
  paymentId: Config['paymentId'];
  private onFetchError: Config['onFetchError'];

  constructor(config: Config) {
    makeAutoObservable(this, {payment: observable.ref}, {autoBind: true});

    this.onFetchError = config.onFetchError;

    if (config.paymentId) {
      this.paymentId = config.paymentId;

      this.fetchPayment();
    } else if (config.payment) {
      this.payment = new PaymentModel(config.payment);
      this.paymentId = config.payment._id;
    }

    this.initialLoad.doneWhen(() => !!this.payment);

    emitter.on(MAKE_REFUND_SUCCESS_EVENT, this.refresh);
    emitter.on(MAKE_REFUND_FAIL_EVENT, this.refresh);
  }

  destruct() {
    emitter.off(MAKE_REFUND_SUCCESS_EVENT, this.refresh);
    emitter.off(MAKE_REFUND_FAIL_EVENT, this.refresh);
  }

  refresh() {
    this.fetchPayment();
  }

  private async fetchPayment() {
    try {
      const payment = (await paymentFetch({
        params: {_id: this.paymentId!, expand: paymentExpandCommon},
      })) as PaymentExpandedCommon;

      runInAction(() => {
        this.payment = new PaymentModel(payment);
      });
    } catch (error) {
      alert.show({
        buttons: [
          {
            label: i18n.t('shared:ok'),
            onClick: this.onFetchError,
          },
        ],
        message: error.message,
        title: i18n.t('shared:message.error'),
      });
    }
  }
}
